import React, { useEffect, useRef, useState } from 'react';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { Columns } from '@reckon-web/columns';
import { Flex } from '@reckon-web/flex';
import { useTheme } from '@reckon-web/theme';

export interface IDraggable {
  id: string;
  pending?: boolean;
}

export interface IDroppable<DraggableType extends IDraggable> {
  id: string;
  draggables: DraggableType[];
}

export const Kanban = <
  DraggableType extends IDraggable,
  DroppableType extends IDroppable<DraggableType>,
>(props: {
  draggables: DraggableType[];
  sort: (draggables: DraggableType[]) => DroppableType[];
  reducer: (
    draggable: DraggableType,
    droppable: DroppableType
  ) => DraggableType;
  resolver: (draggable: DraggableType, droppable: DroppableType) => void;
  Title: React.FC<DroppableType>;
  Body: React.FC<DroppableType & { Draggable: typeof MyDraggable }>;
  columnBgColour?: 'muted' | 'dim';
  testId?: string;
}) => {
  const {
    draggables,
    sort,
    reducer,
    resolver,
    Title,
    Body,
    columnBgColour = 'muted',
    testId = `kanban`,
  } = props;

  const [pendingDraggables, setPendingDraggables] = React.useState<{
    [id: string]: DraggableType;
  }>({});

  const [dragState, setDragState] = React.useState<{
    draggingFromColumn?: string;
    draggingOverColumn?: string;
  }>({});

  const droppables = React.useMemo(
    () =>
      sort(
        draggables.map(
          (draggable) => pendingDraggables[draggable.id] ?? draggable
        )
      ),
    [draggables, sort, pendingDraggables]
  );

  useEffect(() => {
    return monitorForElements({
      onDragStart: ({ source }) => {
        const sourceData = source.data as {
          droppableId: string;
          index: number;
          draggableId: string;
        };
        setDragState({ draggingFromColumn: sourceData.droppableId });
      },
      onDrag: ({ source, location }) => {
        const sourceData = source.data as {
          droppableId: string;
          index: number;
          draggableId: string;
        };
        const target = location.current.dropTargets[0];
        const targetData = target?.data as { droppableId: string } | undefined;

        setDragState({
          draggingFromColumn: sourceData.droppableId,
          draggingOverColumn: targetData?.droppableId,
        });
      },
      onDrop: async ({ source, location }) => {
        const sourceData = source.data as {
          droppableId: string;
          index: number;
          draggableId: string;
        };
        const target = location.current.dropTargets[0];

        setDragState({});

        if (!target) return;

        const destinationData = target.data as { droppableId: string };
        if (destinationData.droppableId === sourceData.droppableId) return;

        const draggable = droppables.find(
          (droppable) => droppable.id === sourceData.droppableId
        )?.draggables[sourceData.index];
        if (!draggable) return;

        const droppable = droppables.find(
          (droppable) => droppable.id === destinationData.droppableId
        );
        if (!droppable) return;

        const pendingDraggable = {
          ...reducer(draggable, droppable),
          pending: true,
        };

        setPendingDraggables((pendingDraggables) => ({
          ...pendingDraggables,
          [draggable.id]: pendingDraggable,
        }));

        await resolver(draggable, droppable);

        setPendingDraggables((draggables) =>
          draggables[draggable.id] === pendingDraggable
            ? (({ [draggable.id]: _, ...draggables }) => draggables)(draggables)
            : draggables
        );
      },
    });
  }, [droppables, reducer, resolver]);

  const theme = useTheme();

  return (
    <Columns
      gap="xsmall"
      height="100%"
      css={{ alignItems: 'unset' }}
      data-test-id={testId}
    >
      {droppables.map((droppable) => (
        <DroppableColumn
          key={droppable.id}
          droppable={droppable}
          isDraggingOver={dragState.draggingOverColumn === droppable.id}
          isDraggingFrom={dragState.draggingFromColumn === droppable.id}
          Title={Title}
          Body={Body}
          columnBgColour={columnBgColour}
          theme={theme}
        />
      ))}
    </Columns>
  );
};

const DroppableColumn = <
  DraggableType extends IDraggable,
  DroppableType extends IDroppable<DraggableType>,
>({
  droppable,
  isDraggingOver,
  isDraggingFrom,
  Title,
  Body,
  columnBgColour,
  theme,
}: {
  droppable: DroppableType;
  isDraggingOver: boolean;
  isDraggingFrom: boolean;
  Title: React.FC<DroppableType>;
  Body: React.FC<DroppableType & { Draggable: typeof MyDraggable }>;
  columnBgColour: 'muted' | 'dim';
  theme: any;
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    return dropTargetForElements({
      element,
      getData: () => ({ droppableId: droppable.id }),
    });
  }, [droppable.id]);

  return (
    <Flex
      direction="column"
      height="auto"
      borderRadius="medium"
      paddingBottom="small"
      css={{
        minWidth: '256px',
        backgroundColor: (() => {
          if (isDraggingOver) return theme.palette.background.activeMuted;
          if (isDraggingFrom) return theme.palette.background.dim;
          return theme.palette.background[columnBgColour];
        })(),
      }}
    >
      <div
        css={{
          position: 'sticky',
          top: 0,
          backgroundColor: 'inherit',
          zIndex: 1,
          borderTopRightRadius: theme.radii.small,
          borderTopLeftRadius: theme.radii.small,
        }}
      >
        <Title {...(droppable as any)} />
      </div>
      <Flex ref={ref} flexGrow={1} direction="column" paddingX="small">
        <Body
          {...(droppable as any)}
          key={droppable.id}
          Draggable={MyDraggable}
        />
      </Flex>
    </Flex>
  );
};

const MyDraggable: React.FC<{
  draggableId: string;
  index: number;
  isDragDisabled?: boolean;
  onClick?: React.MouseEventHandler;
  children?: React.ReactNode;
  droppableId: string;
}> = ({
  draggableId,
  index,
  children,
  isDragDisabled,
  onClick,
  droppableId,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const clickStartPos = useRef<{ x: number; y: number; time: number } | null>(
    null
  );

  useEffect(() => {
    const element = ref.current;
    if (!element || isDragDisabled) return;

    return draggable({
      element,
      getInitialData: () => ({ draggableId, index, droppableId }),
      onDragStart: () => {
        setIsDragging(true);
      },
      onDrop: () => {
        setIsDragging(false);
      },
    });
  }, [draggableId, index, isDragDisabled, droppableId]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (isDragDisabled) return;
    clickStartPos.current = {
      x: e.clientX,
      y: e.clientY,
      time: Date.now(),
    };
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!clickStartPos.current || isDragDisabled) return;

    const deltaX = Math.abs(e.clientX - clickStartPos.current.x);
    const deltaY = Math.abs(e.clientY - clickStartPos.current.y);
    const deltaTime = Date.now() - clickStartPos.current.time;

    // If it moved less than 5px and took less than 300ms, treat it as a click
    if (deltaX < 5 && deltaY < 5 && deltaTime < 300 && onClick) {
      onClick(e as any);
    }

    clickStartPos.current = null;
  };

  return (
    <div
      ref={ref}
      css={{
        cursor: isDragDisabled ? 'default' : 'grab',
        opacity: isDragging ? 0.5 : 1,
      }}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
    >
      {children}
    </div>
  );
};

export type KanbanDraggable = typeof MyDraggable;
